import { useState, useEffect, useMemo } from "react";
import { Item, ItemContent, ItemTitle, ItemActions } from "@/ui/item";
import { Input } from "@/ui/input";
import { Button } from "@/ui/button";
import { Badge } from "@/ui/badge";
import { Label } from "@/ui/label";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/ui/tooltip";
import { IconDeviceFloppy, IconCheck, IconGitPullRequest, IconLock, IconExternalLink } from "@tabler/icons-react";
import { useProjectContext } from "../context/ProjectContext";
import { useUndoAddItem } from "@/api/useGroups";
import { cn } from "@/lib/utils";
import type { ElementWithPendingEdit } from "@/api/common";

// Generate friendly display name from field name
function getFieldLabel(name: string): string {
  const upper = name.toUpperCase();
  if (upper === "TITLE" || upper.includes("TITLE")) return "Title";
  if (upper === "DESCRIPTION" || upper.includes("DESCRIPTION") || upper.includes("DESC")) return "Description";
  if (upper === "SUBTITLE" || upper.includes("SUBTITLE")) return "Subtitle";
  if (upper === "PRICE" || upper.includes("PRICE")) return "Price";
  if (upper === "DATE" || upper.includes("DATE")) return "Date";
  if (upper === "AUTHOR" || upper.includes("AUTHOR")) return "Author";
  if (upper === "NAME" || upper.includes("NAME")) return "Name";
  if (upper === "ROLE" || upper.includes("ROLE")) return "Role";
  if (upper === "BADGE" || upper.includes("BADGE")) return "Badge";
  if (upper === "LABEL" || upper.includes("LABEL")) return "Label";
  if (upper === "TEXT" || upper.includes("TEXT")) return "Content";
  if (upper === "HREF" || upper.includes("HREF") || upper.includes("URL")) return "URL";
  // Fallback: capitalize the name
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

// Map element types to colors (all unique)
const elementTypeColors: Record<string, string> = {
  heading: "text-purple-500",
  text: "text-emerald-500",
  paragraph: "text-sky-500",
  link: "text-blue-500",
  button: "text-orange-500",
  image: "text-pink-500",
  list: "text-teal-500",
  section: "text-indigo-500",
  navigation: "text-cyan-500",
  footer: "text-slate-500",
  hero: "text-violet-500",
  card: "text-amber-500",
  custom: "text-gray-500",
};

interface ElementRowProps {
  element: ElementWithPendingEdit;
  sectionId: string;
  sectionName: string;
  projectId: string;
}

export function ElementRow({ element, sectionId, sectionName, projectId }: ElementRowProps) {
  const { saveEdit, hasEdit, getEdit, removeEdit } = useProjectContext();
  const undoMutation = useUndoAddItem();

  // Check if this element has a pending PR (blocks editing)
  const hasPendingPR = !!element.pendingEdit;

  // Get existing edit if any
  const existingEdit = getEdit(element.id);

  // Detect compound fields from schema
  const compoundFields = element.schema?.fields as Record<string, string> | undefined;
  const hasCompoundFields = compoundFields && Object.keys(compoundFields).length > 0;

  // Build the list of editable fields
  const editableFields = useMemo(() => {
    const fields: Array<{ key: string; label: string; value: string; isHref: boolean }> = [];

    if (hasCompoundFields && compoundFields) {
      // Compound element - show all fields from schema
      for (const [key, val] of Object.entries(compoundFields)) {
        const isHref = key.toUpperCase().includes("HREF") || key.toUpperCase().includes("URL");
        fields.push({
          key,
          label: getFieldLabel(key),
          value: val || "",
          isHref,
        });
      }
    } else {
      // Simple element - show content and href (for links)
      fields.push({
        key: "content",
        label: "Content",
        value: element.currentValue || "",
        isHref: false,
      });

      if (element.type === "link" && element.schema?.href) {
        fields.push({
          key: "href",
          label: "Links to",
          value: element.schema.href || "",
          isHref: true,
        });
      }
    }

    return fields;
  }, [element, hasCompoundFields, compoundFields]);

  // Original values (for simple elements)
  const originalValue = element.currentValue || "";
  const originalHref = element.schema?.href || "";

  // State for all field values
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of editableFields) {
      // Use existing edit value if available
      if (field.key === "content") {
        initial[field.key] = existingEdit?.newValue ?? field.value;
      } else if (field.key === "href") {
        initial[field.key] = existingEdit?.newHref ?? field.value;
      } else {
        // For compound fields, check existingEdit.fields or fall back to original
        initial[field.key] = field.value;
      }
    }
    return initial;
  });

  // Legacy state for backward compatibility
  const value = fieldValues.content ?? "";
  const hrefValue = fieldValues.href ?? "";

  // Check if any field has changed
  const isDirty = useMemo(() => {
    for (const field of editableFields) {
      const currentVal = fieldValues[field.key] || "";
      const originalVal = field.value || "";
      if (currentVal !== originalVal) return true;
    }
    return false;
  }, [editableFields, fieldValues]);

  const hasSavedEdit = hasEdit(element.id);

  // New items (with draft "add" edit) should always be saveable
  const isNewItem = element.isNewItem || false;

  // Sync state when element prop changes (e.g. after refetch)
  useEffect(() => {
    // Only reset if there's no pending edit for this element
    if (!hasEdit(element.id)) {
      const newValues: Record<string, string> = {};
      for (const field of editableFields) {
        newValues[field.key] = field.value;
      }
      setFieldValues(newValues);
    }
  }, [element.id, element.currentValue, element.schema, hasEdit, editableFields]);

  const handleFieldChange = (key: string, newValue: string) => {
    setFieldValues(prev => ({ ...prev, [key]: newValue }));
  };

  const handleSave = () => {
    // Build a summary of compound field values for preview (for new items with schema.fields)
    const compoundFieldSummary = hasCompoundFields && compoundFields
      ? Object.entries(compoundFields).map(([k, v]) => `${k}: ${v}`).join(", ")
      : undefined;

    // Save edit locally (not to backend)
    saveEdit({
      elementId: element.id,
      elementName: element.name || "Untitled Element",
      sectionId,
      sectionName,
      originalValue,
      newValue: value,
      originalHref: element.type === "link" ? originalHref : undefined,
      newHref: element.type === "link" ? hrefValue : undefined,
      sourceFile: element.sourceFile || "",
      sourceLine: element.sourceLine || 0,
      sourceContext: element.sourceContext || "",
      pageUrl: element.pageUrl || "/",
      isNewItem,  // Allow saving new items without modifications
      editType: isNewItem ? "add" : "modify",
      // For add edits, include a summary of the field values for preview
      filledCode: isNewItem && compoundFieldSummary ? compoundFieldSummary : undefined,
    });
  };

  const handleUndo = () => {
    if (!element.groupId) return;

    // Remove from local pending edits if any
    removeEdit(element.id);

    // Call API to undo the add (delete element and draft edit)
    undoMutation.mutate({
      projectId,
      groupId: element.groupId,
      elementId: element.id,
    });
  };

  // Determine button state - check if current values differ from saved edit values
  const savedValue = existingEdit?.newValue ?? originalValue;
  const savedHref = existingEdit?.newHref ?? originalHref;
  const hasUnsavedChanges = (value !== savedValue) || (hrefValue !== savedHref);
  const needsSave = isDirty && !hasSavedEdit;

  return (
    <Item
      variant="outline"
      className={cn(
        "flex-col items-stretch transition-colors",
        isNewItem && !hasSavedEdit && "border-l-2 border-l-green-500 bg-green-500/5",
        hasSavedEdit && "border-l-2 border-l-primary bg-primary/5",
        hasPendingPR && "border-l-2 border-l-amber-500 bg-amber-500/5"
      )}
    >
      <div className="flex items-center gap-3 w-full">
        <ItemContent className="flex-1">
          <div className="flex items-center gap-2">
            <ItemTitle>{element.name || "Untitled Element"}</ItemTitle>
            {hasPendingPR && (
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="warning" className="text-[10px] gap-1">
                    <IconGitPullRequest className="w-3 h-3" />
                    PR #{element.pendingEdit!.pullRequest.githubPrNumber}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-sm p-3">
                  <div className="flex items-center gap-1.5 text-zinc-400 text-xs mb-2">
                    <IconGitPullRequest className="w-3.5 h-3.5" />
                    <span className="font-semibold text-zinc-100">{element.pendingEdit!.pullRequest.title}</span>
                  </div>

                  <div className="font-mono text-xs space-y-0.5">
                    {/* Content change */}
                    {element.currentValue !== element.pendingEdit!.newValue && (
                      <>
                        <div className="text-green-400">
                          <span className="text-green-500/70 select-none">+ </span>
                          {element.pendingEdit!.newValue}
                        </div>
                        <div className="text-red-400">
                          <span className="text-red-500/70 select-none">- </span>
                          {element.currentValue}
                        </div>
                      </>
                    )}

                    {/* Href change for links */}
                    {element.pendingEdit!.oldHref && element.pendingEdit!.newHref && element.pendingEdit!.oldHref !== element.pendingEdit!.newHref && (
                      <>
                        {element.currentValue !== element.pendingEdit!.newValue && (
                          <div className="border-t border-zinc-700 my-1.5" />
                        )}
                        <div className="text-green-400 break-all">
                          <span className="text-green-500/70 select-none">+ </span>
                          {element.pendingEdit!.newHref}
                        </div>
                        <div className="text-red-400 break-all">
                          <span className="text-red-500/70 select-none">- </span>
                          {element.pendingEdit!.oldHref}
                        </div>
                      </>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
            {!hasPendingPR && isNewItem && !hasSavedEdit && (
              <Badge variant="default" className="text-[10px] bg-green-500/20 text-green-400">
                New
              </Badge>
            )}
            {!hasPendingPR && hasSavedEdit && (
              <Badge variant="default" className="text-[10px] bg-primary/20 text-primary">
                Edited
              </Badge>
            )}
          </div>
        </ItemContent>

        {element.type && (
          <Badge
            variant="outline"
            className={cn(
              "text-[10px] uppercase tracking-wide",
              elementTypeColors[element.type] || "text-gray-500"
            )}
          >
            {element.type}
          </Badge>
        )}

        <ItemActions>
          {hasPendingPR ? (
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger>
                  <IconLock className="w-3.5 h-3.5 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  Editing blocked until PR is merged or closed
                </TooltipContent>
              </Tooltip>
              <Button
                size="sm"
                variant="ghost"
                className="hover:bg-zinc-800 hover:text-zinc-100 text-zinc-400 transition-colors"
                render={
                  <a
                    href={element.pendingEdit!.pullRequest.githubPrUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  />
                }
              >
                View PR
                <IconExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {/* Undo button for new items */}
              {isNewItem && element.groupId && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleUndo}
                  disabled={undoMutation.isPending}
                >
                  Undo
                </Button>
              )}
              <Button
                size="sm"
                variant={needsSave || hasUnsavedChanges || isNewItem ? "default" : hasSavedEdit ? "outline" : "ghost"}
                onClick={handleSave}
                disabled={!isDirty && !isNewItem}
              >
                {hasSavedEdit && !hasUnsavedChanges ? (
                  <IconCheck className="w-3.5 h-3.5" />
                ) : (
                  <IconDeviceFloppy className="w-3.5 h-3.5" />
                )}
                {hasSavedEdit && !hasUnsavedChanges ? "Saved" : "Save"}
              </Button>
            </div>
          )}
        </ItemActions>
      </div>

      {/* Dynamic Field Inputs */}
      <div className="flex flex-col gap-2 w-full pt-1">
        {editableFields.map((field) => (
          <div key={field.key} className="flex flex-col gap-1">
            <Label
              htmlFor={`input-${element.id}-${field.key}`}
              className="text-xs text-muted-foreground"
            >
              {field.label}
            </Label>
            <Input
              id={`input-${element.id}-${field.key}`}
              value={hasPendingPR ? field.value : (fieldValues[field.key] || "")}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={field.isHref ? "Target URL (e.g. /features, https://...)" : "Enter text"}
              disabled={hasPendingPR}
              className={cn(
                hasSavedEdit && "border-primary/50",
                hasPendingPR && "opacity-60 cursor-not-allowed",
                field.isHref && "font-mono"
              )}
            />
          </div>
        ))}
      </div>
    </Item>
  );
}
